import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, UserCog, Users, Shield, Download, Upload } from "lucide-react";
import { toast } from "sonner";

export default function People({ event, slug, refresh }) {
  const [sub, setSub] = useState("gms");
  return (
    <div>
      <div className="flex gap-1 mb-6 border-b border-border">
        {[["gms", "Game Masters"], ["players", "Players"], ["teams", "Teams"]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`people-${k}`} className={`px-4 py-2 text-sm font-semibold border-b-2 ${sub === k ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      {sub === "gms" && <GMs slug={slug} />}
      {sub === "players" && <Players slug={slug} />}
      {sub === "teams" && <Teams slug={slug} event={event} refresh={refresh} />}
    </div>
  );
}

function GMs({ slug }) {
  const [gms, setGms] = useState(null);
  const [zones, setZones] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", assigned_zones: [] });
  const load = () => api.get(`/events/${slug}/gamemasters`).then((r) => setGms(r.data));
  useEffect(() => { load(); api.get(`/events/${slug}/zones`).then((r) => setZones(r.data)); /* eslint-disable-next-line */ }, [slug]);
  const save = async () => {
    if (!f.name || !f.email) { toast.error("Name and email required"); return; }
    await api.post(`/events/${slug}/gamemasters`, f);
    toast.success("Game Master added — they log in with this email"); setOpen(false); setF({ name: "", email: "", assigned_zones: [] }); load();
  };
  const del = async (id) => { await api.delete(`/events/${slug}/gamemasters/${id}`); load(); };
  const toggleZone = (zid) => setF((s) => ({ ...s, assigned_zones: s.assigned_zones.includes(zid) ? s.assigned_zones.filter((z) => z !== zid) : [...s.assigned_zones, zid] }));
  if (!gms) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;
  return (
    <div>
      <div className="flex justify-between items-center mb-4"><h2 className="font-head font-bold text-lg flex items-center gap-2"><UserCog className="w-5 h-5" />Game Masters</h2><Button className="rounded-full font-bold" onClick={() => setOpen(true)} data-testid="add-gm"><Plus className="w-4 h-4 mr-1" />Add</Button></div>
      <div className="grid md:grid-cols-2 gap-3">
        {gms.map((g) => (
          <div key={g.gm_id} className="brut p-4 flex items-center justify-between">
            <div><p className="font-bold">{g.name}</p><p className="text-xs text-muted-foreground">{g.email} · {g.assigned_zones.length} zones</p></div>
            <Button size="icon" variant="ghost" className="text-primary" onClick={() => del(g.gm_id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {gms.length === 0 && <p className="text-sm text-muted-foreground">No game masters yet. You (the organizer) can always run every zone.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent>
        <DialogHeader><DialogTitle className="font-head">Add Game Master</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1 block">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="gm-name" /></div>
          <div><Label className="mb-1 block">Login email (Google)</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="gm-email" /></div>
          <div><Label className="mb-1 block">Assign zones</Label>
            <div className="flex flex-wrap gap-2">{zones.map((z) => <button key={z.zone_id} onClick={() => toggleZone(z.zone_id)} className={`px-3 py-1.5 rounded-full border text-sm ${f.assigned_zones.includes(z.zone_id) ? "bg-primary text-white border-primary" : "border-border"}`}>{z.name}</button>)}</div>
          </div>
          <Button className="w-full rounded-full font-bold" onClick={save} data-testid="gm-save">Add Game Master</Button>
        </div>
      </DialogContent></Dialog>
    </div>
  );
}

function Players({ slug }) {
  const [players, setPlayers] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [f, setF] = useState({ name: "", contact: "", participant_id: "", team: "" });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => api.get(`/events/${slug}/players`).then((r) => setPlayers(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const downloadTemplate = async (format) => {
    try {
      const r = await api.get(`/events/${slug}/participants/template`, { params: { format }, responseType: "blob" });
      dl(r.data, `participants_template.${format === "xlsx" ? "xlsx" : "csv"}`);
    } catch { toast.error("Could not download template"); }
  };
  const exportExcel = async () => {
    try {
      const r = await api.get(`/events/${slug}/participants/export`, { params: { format: "xlsx" }, responseType: "blob" });
      dl(r.data, `${slug}_participants.xlsx`);
      toast.success("Exported all participants");
    } catch { toast.error("Export failed"); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setImporting(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/events/${slug}/participants/import`, fd);
      setResult(r.data);
      if (r.data.imported) toast.success(`Pre-booked ${r.data.imported} participant(s)`);
      if (r.data.failed) toast.warning(`${r.data.failed} row(s) skipped`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };
  const addManual = async () => {
    if (!f.name.trim() || !f.contact.trim()) { toast.error("Name and contact required"); return; }
    try {
      const r = await api.post(`/events/${slug}/participants`, f);
      toast.success(`Added — code ${r.data.serial_no}`);
      setAddOpen(false); setF({ name: "", contact: "", participant_id: "", team: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add"); }
  };

  if (!players) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><Users className="w-5 h-5" />Players ({players.length})</h2>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" className="rounded-full" onClick={() => downloadTemplate("csv")} data-testid="pt-template-csv"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
          <Button size="sm" variant="secondary" className="rounded-full" onClick={() => downloadTemplate("xlsx")} data-testid="pt-template-xlsx"><Download className="w-3.5 h-3.5 mr-1" />Excel</Button>
          <label className="inline-flex">
            <input type="file" accept=".csv,.xlsx" className="hidden" onChange={onFile} data-testid="pt-import-input" disabled={importing} />
            <span className={`inline-flex items-center px-4 h-9 rounded-full font-bold text-sm cursor-pointer bg-secondary ${importing ? "opacity-60 pointer-events-none" : ""}`} data-testid="pt-import-btn">{importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Import</span>
          </label>
          <Button size="sm" className="rounded-full font-bold" onClick={() => setAddOpen(true)} data-testid="pt-add"><Plus className="w-4 h-4 mr-1" />Add</Button>
          <Button size="sm" variant="outline" className="rounded-full font-bold" onClick={exportExcel} data-testid="pt-export"><Download className="w-3.5 h-3.5 mr-1" />Export</Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-3">Pre-book participants by import or manual add. Each gets a unique code — share it so they can scan the QR and start playing instantly.</p>

      {result && (
        <div className="brut p-3 mb-4 text-xs" data-testid="pt-import-result">
          <p className="font-semibold text-neon-green">{result.imported} pre-booked · <span className="text-primary">{result.failed} skipped</span> · {result.total} rows</p>
          {result.errors?.length > 0 && <ul className="mt-1 max-h-24 overflow-y-auto text-muted-foreground space-y-0.5">{result.errors.map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}</ul>}
        </div>
      )}

      <div className="brut overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-secondary"><tr><th className="text-left p-3">Code</th><th className="text-left p-3">Player</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Team</th><th className="text-left p-3">Status</th><th className="text-right p-3">Cash</th></tr></thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.player_id} className="border-t border-border" data-testid={`player-${p.player_id}`}>
                <td className="p-3 font-mono-num font-bold">{p.serial_no || "—"}</td>
                <td className="p-3 font-semibold">{p.name}</td>
                <td className="p-3 text-muted-foreground">{p.contact}</td>
                <td className="p-3 text-muted-foreground">{p.team_name || "—"}</td>
                <td className="p-3">{p.activated ? <span className="text-neon-green font-semibold">Joined</span> : <span className="text-muted-foreground">Pre-booked</span>}</td>
                <td className="p-3 text-right font-mono-num text-gold font-bold">{p.total_xp}</td>
              </tr>
            ))}
            {players.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No players yet. Import a roster or add participants to pre-book them.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}><DialogContent>
        <DialogHeader><DialogTitle className="font-head">Add participant</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1 block">Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pt-f-name" /></div>
          <div><Label className="mb-1 block">Contact (email or phone)</Label><Input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} data-testid="pt-f-contact" /></div>
          <div><Label className="mb-1 block">Participant ID (optional)</Label><Input value={f.participant_id} onChange={(e) => setF({ ...f, participant_id: e.target.value })} data-testid="pt-f-pid" /></div>
          <div><Label className="mb-1 block">Team name (optional)</Label><Input value={f.team} onChange={(e) => setF({ ...f, team: e.target.value })} data-testid="pt-f-team" /></div>
          <Button className="w-full rounded-full font-bold" onClick={addManual} data-testid="pt-f-save">Add & generate code</Button>
        </div>
      </DialogContent></Dialog>
    </div>
  );
}

function dl(data, filename) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
}

function Teams({ slug, event, refresh }) {
  const [teams, setTeams] = useState(null);
  const [name, setName] = useState("");
  const load = () => api.get(`/events/${slug}/teams`).then((r) => setTeams(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);
  const toggle = async (v) => { await api.put(`/events/${slug}`, { teams_enabled: v }); toast.success(v ? "Teams enabled" : "Teams disabled"); refresh(); };
  const add = async () => { if (!name.trim()) return; await api.post(`/events/${slug}/teams`, { name, color: "#FF3B30" }); setName(""); load(); };
  const del = async (id) => { await api.delete(`/events/${slug}/teams/${id}`); load(); };
  if (!teams) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;
  return (
    <div>
      <div className="brut p-4 flex items-center justify-between mb-4">
        <div><p className="font-bold">Team mode</p><p className="text-xs text-muted-foreground">Let players join teams and compete on a team leaderboard.</p></div>
        <Switch checked={event.teams_enabled} onCheckedChange={toggle} data-testid="teams-toggle" />
      </div>
      {event.teams_enabled && (<>
        <div className="flex gap-2 mb-4"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Team name" data-testid="team-name" /><Button className="rounded-full" onClick={add} data-testid="add-team"><Plus className="w-4 h-4" /></Button></div>
        <div className="grid md:grid-cols-2 gap-3">
          {teams.map((t) => (
            <div key={t.team_id} className="brut p-4 flex items-center justify-between">
              <div className="flex items-center gap-2"><span className="w-4 h-4 rounded-full" style={{ background: t.color }} /><div><p className="font-bold">{t.name}</p><p className="text-xs text-muted-foreground">{t.member_count} members · {t.team_xp} Cash</p></div></div>
              <Button size="icon" variant="ghost" className="text-primary" onClick={() => del(t.team_id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      </>)}
    </div>
  );
}
